import { Component, ElementRef, ViewChild, OnInit } from '@angular/core';
import { UntypedFormControl, Validators, UntypedFormGroup } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';

import { CommonService } from '../../services/common.service';
import { ReportsService } from '../../services/reports.service';
import { GuideAndBucketContentService } from '../../services/guide-and-bucket-content.service';
import { MediaTypes } from '../../model/MediaTypes';

interface ImageData {
  fileName?: string;
  imageSrc: string;
  imageStream: any;
  firstName?: string;
  lastName?: string;
  pin?: string;
  fileExtension: string;
  isApproved?: boolean;
  uploadedDate?: string;
  approvedDate?: string;
  documentTitle?: string;
  eventType?: string;
  eventDate?: string;

  date?: string;
  time?: string;

  detailTitle?: string;
  details?: string;

  // Licence checking
  approved?: string;
  approved_description?: string;

  // External Training
  title?: string;
  reason?: string;
  dateAssigned?: string;
  dateCompleted?: string;
  rating?: string;
  organisation?: string;

  // Internal use only
  mediaType?: string | null;
  formInfo?: FormMessage;
  downloadUrl?: string | ArrayBuffer;
  generatedFileName?: string;
}

interface EndpointCrud {
  GET: string;
  POST: string;
  PUT: string;
  PATCH: string;
  DELETE: string;
}

type MessageType = 'success' | 'error' | 'warning' | 'info' | 'blank';

interface FormMessage {
  message: string;
  type: MessageType;
}

@Component({
  selector: 'image-viewer-dialog',
  templateUrl: './image-viewer.component.html',
  styleUrls: ['./image-viewer.component.scss'],
})
export class ImageViewerComponent implements OnInit {
  mediaTypes = MediaTypes;
  initialFormLoad: boolean = true;

  selectedImage: ImageData;
  maxIndex: number;
  minIndex: number = 0;
  currentIndex: number = 0;

  isCrashImages: boolean = false;
  isNonUkLicenceDocs: boolean = false;
  isExternalTrainingDocs: boolean = false;
  isDerDocuments: boolean = false;
  canPostData: boolean;

  @ViewChild('imageElementContainer', { static: false })
  imageElementContainer: ElementRef;

  imageData: Array<ImageData>;
  imageDataEndpoint: EndpointCrud;
  resolvedEndpoints: { GET?: string; POST?: string; PUT?: string; PATCH?: string; DELETE?: string };

  formInfo: FormMessage = { message: '', type: 'blank' };
  formChangedMessage: string = '';
  requiresGridRefresh = false;

  licenceApprovalForm: UntypedFormGroup;

  documentTypes = ['.pdf'];
  imageExtensions = ['.apng', '.avif', '.gif', '.jpg', '.jpeg', '.jfif', '.pjpeg', '.pjp', '.png', '.webp', '.tiff'];
  videoExtensions = ['.avi', '.mp4', '.mpeg', '.ogv', '.ts', '.webm'];
  audioExtensions = ['.aac', '.cda', '.mid', '.midi', '.mp3', '.oga', '.opus', '.wav', '.weba'];

  constructor(
    public cService: CommonService,
    private rptService: ReportsService,
    private gBucketService: GuideAndBucketContentService,
    private dialogRef: MatDialogRef<ImageViewerComponent>
  ) {}

  ngOnInit() {
    setTimeout(() => {
      console.log('%c Selected Row & Endpoint data', 'color: salmon', {
        selectedRow: this.rptService.selectedRows[0],
        endpoints: this.rptService.commonReportDrilldownEndpoints,
      });

      if (
        this.rptService.commonReportDrilldownEndpoints &&
        (this.rptService.commonReportDrilldownEndpoints.images ||
          this.rptService.commonReportDrilldownEndpoints.documents)
      ) {
        this.imageDataEndpoint =
          this.rptService.commonReportDrilldownEndpoints.images ||
          this.rptService.commonReportDrilldownEndpoints.documents;

        this.resolvedEndpoints = {};

        for (const httpMethod of Object.keys(this.imageDataEndpoint)) {
          const resolved = this.cService.resolveUriSegments(
            this.imageDataEndpoint[httpMethod],
            this.rptService.selectedRows[0]
          );

          this.resolvedEndpoints[httpMethod] = resolved.uri;

          if (httpMethod == 'GET' && !resolved.success) {
            this.cService.showToast(this.cService.readyTranslations.messagesAndWarnings.requestedAssetNotAvailable);
            return;
          }
        }

        if (this.imageDataEndpoint.PATCH || this.imageDataEndpoint.POST || this.imageDataEndpoint.PUT) {
          this.canPostData = true;
        }

        const getEndpoint = this.resolvedEndpoints.GET;

        if (getEndpoint.includes('collision')) {
          this.isCrashImages = true;
        }
        if (getEndpoint.includes('forms/data/DER')) {
          this.isDerDocuments = true;
        } else if (getEndpoint.includes('licence') && getEndpoint.includes('non_uk')) {
          this.isNonUkLicenceDocs = true;
        } else if (getEndpoint.includes('external') && getEndpoint.includes('training')) {
          this.isExternalTrainingDocs = true;
        }

        this.prepareForm();
        this.getImageDataForSelectedDriver(getEndpoint);
      }
    }, 150);

    this.dialogRef.beforeClosed().subscribe({
      next: () => {
        if (this.requiresGridRefresh) {
          this.rptService.triggerDriverPerformanceDialogClosedObservable(this.rptService.endpointFilterValue, false);
        }
      },
    });
  }

  private getImageDataForSelectedDriver(imageEndpoint: string) {
    this.cService.commonGet(imageEndpoint).subscribe({
      next: (response: { data: Array<ImageData> }) => {
        console.log('%c Image and details', 'color: yellowgreen', response);

        this.imageData = response.data;
        this.selectedImage = this.imageData[this.currentIndex];
        this.maxIndex = this.getMaxIndex();

        this.getMediaType();
        this.setCurrentFormData();
        this.getAndAssignImageStream();
        this.generateFileName();
      },
    });
  }

  private getAndAssignImageStream() {
    if (!this.selectedImage.imageStream) {
      this.gBucketService.getDocument(this.selectedImage.imageSrc).subscribe({
        next: (streamResp) => {
          if (!streamResp) {
            this.cService.showToast(this.cService.readyTranslations.messagesAndWarnings.requestedAssetNotAvailable);
            return;
          }

          const imageStreamReader = new FileReader();

          imageStreamReader.addEventListener(
            'load',
            () => {
              this.selectedImage.imageStream = imageStreamReader.result;
              this.imageData[this.currentIndex].imageStream = imageStreamReader.result;

              this.selectedImage.downloadUrl = this.selectedImage.imageStream;
              this.imageData[this.currentIndex].downloadUrl = this.imageData[this.currentIndex].imageStream;

              if (this.selectedImage.fileExtension === '.pdf') {
                const pdfUrl = 'data:application/pdf;base64,' + btoa(this.selectedImage.imageSrc);

                this.selectedImage.downloadUrl = pdfUrl;
                this.imageData[this.currentIndex].downloadUrl = pdfUrl;
              }
            },
            false
          );

          imageStreamReader.readAsDataURL(streamResp);
        },
      });
    }
  }

  navigateImage(next: boolean = false) {
    if (next) {
      this.navigateToNextImage();
    } else {
      this.navigateToPreviousImage();
    }
  }

  private navigateToNextImage() {
    this.maxIndex = this.getMaxIndex();

    if (this.currentIndex !== this.maxIndex) {
      this.currentIndex++;
      this.selectedImage = this.imageData[this.currentIndex];

      this.getMediaType();
      this.setCurrentFormData();
      this.getAndAssignImageStream();
      this.generateFileName();
    }
  }

  private navigateToPreviousImage() {
    if (this.currentIndex !== this.minIndex) {
      this.currentIndex--;
      this.selectedImage = this.imageData[this.currentIndex];

      this.getMediaType();
      this.setCurrentFormData();
      this.getAndAssignImageStream();
      this.generateFileName();
    }
  }

  private getMaxIndex(): number {
    return this.imageData.length - 1;
  }

  private prepareForm() {
    if (this.canPostData && this.isNonUkLicenceDocs) {
      this.licenceApprovalForm = new UntypedFormGroup({
        approved: new UntypedFormControl(null, Validators.required),
        approved_description: new UntypedFormControl({
          value: '',
          disabled: false,
        }),
      });

      this.watchFormChanges();
    }
  }

  private watchFormChanges() {
    this.licenceApprovalForm.valueChanges.subscribe({
      next: () => {
        if (!this.initialFormLoad) {
          const message = this.cService.readyTranslations.messagesAndWarnings.pressSubmitToSave;
          this.setAsyncFormChangedWarning(message);
        }

        this.initialFormLoad = false;
      },
    });
  }

  private setCurrentFormData() {
    if (this.canPostData && this.isNonUkLicenceDocs) {
      const matchedFormData = {};

      for (const key in this.licenceApprovalForm.value) {
        if (this.selectedImage.hasOwnProperty(key)) {
          matchedFormData[key] = this.selectedImage[key];
        }
      }
      this.licenceApprovalForm.enable();
      if (this.selectedImage.approved !== null) {
        this.licenceApprovalForm.disable();
      }

      this.licenceApprovalForm.setValue(matchedFormData);
      this.setAsyncFormChangedWarning('');
    }
  }

  private persistSubmittedFormData() {
    this.imageData[this.currentIndex] = this.selectedImage;
  }

  private mergeFormDataWithCurrentSelection() {
    for (const key in this.licenceApprovalForm.value) {
      if (this.selectedImage.hasOwnProperty(key)) {
        this.selectedImage[key] = this.licenceApprovalForm.value[key];
      }
    }
  }

  approveOrDisapproveLicence() {
    this.mergeFormDataWithCurrentSelection();

    console.log('%c Persisted Data', 'color: salmon', {
      licenceApprovedForm: this.licenceApprovalForm.value,
      imageData: this.imageData,
      selectedImage: this.selectedImage,
    });

    const patchData = {
      status: this.selectedImage.approved,
      description: this.selectedImage.approved_description,
    };

    const resolvedImageDataPatchEndpoint = this.cService.resolveUriSegments(
      this.imageDataEndpoint.PATCH,
      this.selectedImage
    );

    this.cService.commonPatch(resolvedImageDataPatchEndpoint.uri, patchData).subscribe({
      next: (response: any) => {
        console.log('%c PATCH Response', 'color: salmon', response);
        this.persistSubmittedFormData();

        this.setFormInfoMessage('', 'blank');
        this.setAsyncFormChangedWarning('');
        this.requiresGridRefresh = true;
        this.licenceApprovalForm.disable();
      },

      error: () => {
        const errorMessage = this.cService.readyTranslations.messagesAndWarnings.failedToProcessData;

        this.setFormInfoMessage(errorMessage, 'error');
        this.setAsyncFormChangedWarning('');
      },
    });
  }

  private setFormInfoMessage(message: string, messageType: MessageType) {
    if (this.canPostData) {
      this.formInfo = {
        message: message,
        type: messageType,
      };

      // Persist form message across available image/ doc data
      this.selectedImage.formInfo = this.formInfo;
    }
  }

  private setAsyncFormChangedWarning(message: string) {
    setTimeout(() => {
      this.formChangedMessage = message;
    });
  }

  private generateFileName(): void {
    let fileName = this.selectedImage.fileName;

    if (
      this.selectedImage.firstName &&
      this.selectedImage.lastName &&
      this.selectedImage.date &&
      this.selectedImage.time
    ) {
      fileName = `
                ${this.selectedImage.firstName}
                ${this.selectedImage.lastName} |
                ${this.selectedImage.date}
                ${this.selectedImage.time}
            `;
    }

    this.selectedImage.generatedFileName = fileName;
    this.imageData[this.currentIndex].generatedFileName = fileName;
  }

  private getMediaType() {
    let mediaType = MediaTypes.image;
    const fileExtension = this.imageData[this.currentIndex].fileExtension.toLowerCase();

    if (this.audioExtensions.includes(fileExtension) || this.audioExtensions.includes(`.${fileExtension}`)) {
      mediaType = MediaTypes.audio;
    }

    if (this.videoExtensions.includes(fileExtension) || this.videoExtensions.includes(`.${fileExtension}`)) {
      mediaType = MediaTypes.video;
    }

    if (this.documentTypes.includes(fileExtension) || this.documentTypes.includes(`.${fileExtension}`)) {
      mediaType = MediaTypes.document;
    }

    this.imageData[this.currentIndex].mediaType = mediaType;
  }
}
